import React, { useRef } from 'react';
import { AccountsSection, TeamMembersSection, TransactionTypesSection } from './FilterSections';
import { CollectionIcon, CurrencyDollar, TeamIcon } from '@/components/Icons';
import cn from '@/utils/cn';
import { TWalletData } from '../types/WalletData';
import { TTeamMember } from '@/context/types/Business';
import { TTransactionFilter } from '../data/transactionTypes';
import { TCheckBoxLabel } from '@/components/Checkbox';
import { TFilters } from '../types/Filter';
import { generateTransactionTypeId } from '../utils/generateTransactionId';
import { useClickOutside } from '@/hooks/useClickOutside';
import useAuth from '@/hooks/useAuth';
import { AccountTypeEnum } from '@/context/types/Account';

export const BANK_ACCOUNTS_FILTER_NAME = 'bank-accounts';
export const TEAM_MEMBERS_FILTER_NAME = 'team-members';
export const TRANSACTION_TYPES_FILTER_NAME = 'transaction-types';
export const filtersName = [
  BANK_ACCOUNTS_FILTER_NAME,
  TEAM_MEMBERS_FILTER_NAME,
  TRANSACTION_TYPES_FILTER_NAME
];

interface FilterComponentProps {
  focusFilter: string | undefined;
  onFocusFilter: (filterName: string) => void;
  onCheckboxChange: (options: {
    option: TCheckBoxLabel;
    isChecked: boolean;
    filterName: keyof TFilters;
  }) => void;
  onSelectAll: (filterName: keyof TFilters, options: string[]) => void;
  onUnselectAll: (filterName: keyof TFilters) => void;
  options: {
    wallets: TWalletData[];
    teamMembers: TTeamMember[];
    transactionTypes: TTransactionFilter[];
  };
  checkedOptions: {
    transactionsTypes: (string | number)[];
    teamMembers: (string | number)[];
    wallets: (string | number)[];
  };
  onApplyWalletFilter: () => void;
  onApplyTeamMemberFilter: () => void;
  onApplyTransactionTypeFilter: () => void;
  onForceClose?: () => void;
}

const NavItem: React.FC<{
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
}> = ({ label, icon, active, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      'flex items-center justify-between w-full px-2 py-2 rounded text-xs font-medium text-[#010B3B]',
      active ? 'bg-[#F7F7F2] border border-[#E5E7EB]' : 'hover:bg-[#F7F7F2]'
    )}>
    <span className="flex items-center gap-1.5">
      {icon}
      <span>{label}</span>
    </span>
    {active && (
      <svg width="6" height="10" viewBox="0 0 6 10" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M1 1L5 5L1 9"
          stroke="#010B3B"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )}
  </button>
);

const FilterComponent: React.FC<FilterComponentProps> = ({
  focusFilter,
  onFocusFilter,
  onSelectAll,
  onUnselectAll,
  onCheckboxChange,
  options,
  checkedOptions,
  onApplyWalletFilter,
  onApplyTeamMemberFilter,
  onApplyTransactionTypeFilter,
  onForceClose
}) => {
  const { accountType } = useAuth()!;

  const filterRef = useRef<HTMLDivElement | null>(null);
  useClickOutside(filterRef, () => {
    if (onForceClose) {
      onForceClose();
    }
  });

  const activeFilter =
    focusFilter && filtersName.includes(focusFilter) ? focusFilter : BANK_ACCOUNTS_FILTER_NAME;

  const handleAccountsFilterChange = (option: TCheckBoxLabel, isChecked: boolean) => {
    onCheckboxChange({ option, isChecked, filterName: 'wallets' });
  };

  const handleTeamMemberFilterChange = (option: TCheckBoxLabel, isChecked: boolean) => {
    onCheckboxChange({ option, isChecked, filterName: 'teamMembers' });
  };

  const handleTransactionFilterChange = (option: TCheckBoxLabel, isChecked: boolean) => {
    onCheckboxChange({ option, isChecked, filterName: 'transactionsTypes' });
  };

  const handleOnToggleSelectAllWallets = (isAllSelected: boolean) => {
    isAllSelected
      ? onUnselectAll('wallets')
      : onSelectAll(
          'wallets',
          options.wallets.map((el) => el.id)
        );
  };

  const handleOnToggleSelectAllTeamMembers = (isAllSelected: boolean) => {
    isAllSelected
      ? onUnselectAll('teamMembers')
      : onSelectAll(
          'teamMembers',
          options.teamMembers.map((el) => el.id)
        );
  };

  const handleOnToggleSelectAllTransactionTypes = (isAllSelected: boolean) => {
    isAllSelected
      ? onUnselectAll('transactionsTypes')
      : onSelectAll(
          'transactionsTypes',
          options.transactionTypes.map((el) => generateTransactionTypeId(el))
        );
  };

  return (
    <div
      ref={filterRef}
      className="flex bg-white rounded-lg overflow-hidden shadow-[0px_4px_6px_-1px_rgba(0,0,0,0.10),0px_2px_4px_-2px_rgba(0,0,0,0.05)]">
      {/* Left sidebar */}
      <div className="w-[177px] bg-[#FBFBFA] py-4 px-3 flex flex-col gap-3 shrink-0">
        <NavItem
          label="Accounts"
          icon={<CollectionIcon />}
          active={activeFilter === BANK_ACCOUNTS_FILTER_NAME}
          onClick={() => onFocusFilter(BANK_ACCOUNTS_FILTER_NAME)}
        />
        <NavItem
          label="Transaction types"
          icon={<CurrencyDollar />}
          active={activeFilter === TRANSACTION_TYPES_FILTER_NAME}
          onClick={() => onFocusFilter(TRANSACTION_TYPES_FILTER_NAME)}
        />
        {accountType === AccountTypeEnum.BUSINESS && (
          <NavItem
            label="Team members"
            icon={<TeamIcon />}
            active={activeFilter === TEAM_MEMBERS_FILTER_NAME}
            onClick={() => onFocusFilter(TEAM_MEMBERS_FILTER_NAME)}
          />
        )}
      </div>

      {/* Right content panel */}
      <div className="py-4 px-2 flex flex-col min-w-[320px] max-h-[444px]">
        {activeFilter === BANK_ACCOUNTS_FILTER_NAME && (
          <AccountsSection
            options={options.wallets}
            checkedOptions={checkedOptions.wallets}
            onCheckboxClick={handleAccountsFilterChange}
            onSelectAllToggle={handleOnToggleSelectAllWallets}
            onApply={onApplyWalletFilter}
            onReset={() => onUnselectAll('wallets')}
          />
        )}
        {activeFilter === TRANSACTION_TYPES_FILTER_NAME && (
          <TransactionTypesSection
            options={options.transactionTypes}
            checkedOptions={checkedOptions.transactionsTypes}
            onCheckboxClick={handleTransactionFilterChange}
            onSelectAllToggle={handleOnToggleSelectAllTransactionTypes}
            onApply={onApplyTransactionTypeFilter}
            onReset={() => onUnselectAll('transactionsTypes')}
          />
        )}
        {activeFilter === TEAM_MEMBERS_FILTER_NAME && accountType === AccountTypeEnum.BUSINESS && (
          <TeamMembersSection
            options={options.teamMembers}
            checkedOptions={checkedOptions.teamMembers}
            onCheckboxClick={handleTeamMemberFilterChange}
            onSelectAllToggle={handleOnToggleSelectAllTeamMembers}
            onApply={onApplyTeamMemberFilter}
            onReset={() => onUnselectAll('teamMembers')}
          />
        )}
      </div>
    </div>
  );
};

export default FilterComponent;
